import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import WaitingIndicator from '../WaitingIndicator/WaitingIndicator';
import Notes from '../Notes/Notes'; // Adjust path if needed
import RelatedDocumentsSection from '../RelatedDocumentsSection/RelatedDocumentsSection';
import RelatedEntitiesSection from '../RelatedEntitiesSection/RelatedEntitiesSection';
import './StandardDetailsLayout.css';

/**
 * StandardDetailsLayout
 * 
 * A unified layout component for all detail screens (Travel, Accommodation, Places, Days).
 * Enforces a consistent structure with Header, Main Content, Resources, Related Info, and Notes.
 */
export const StandardDetailsLayout = ({
  title,
  subtitle,
  onBack,
  actions = [],
  headerActions,
  loading = false,
  error = null,
  children,
  // Slots for specific standardized sections
  resourcesSection = null,

  // Data for standardized sections
  notes,
  onSaveNotes,
  lastEditedTime,

  // Related data props
  contentBlocks,
  tripId,
  onUnlink,

  // Optional Overrides
  showRelatedDocuments = true,
  showRelatedEntities = true,
  className = ''
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className={`standard-details-layout ${className}`}>
        <WaitingIndicator message="Loading details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`standard-details-layout ${className}`}>
        <div className="standard-details-header">
          <div className="header-content">
            <div className="header-left">
              <button className="back-button" onClick={handleBack} aria-label="Go back">
                <FaArrowLeft />
              </button>
              <div className="title-area">
                <h2>Error</h2>
              </div>
            </div>
          </div>
        </div>
        <div className="main-content-scroll-area">
          <div className="error-message" style={{ padding: '2rem', color: 'red', textAlign: 'center' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`standard-details-layout ${className}`}>
      {/* Header Area - Matching original PlaceDetails structure */}
      <header className="standard-details-header">
        <div className="place-title-section">
          <div className="title-display">
            {/* Title on left */}
            <h1 className="title-area">
              {title}
              {subtitle && <p className="subtitle">{subtitle}</p>}
            </h1>

            {/* Action buttons on right - direct children of title-display to match old structure */}
            {headerActions ? (
              headerActions
            ) : (
              actions.length > 0 && actions.map((action, index) => (
                <button
                  key={index}
                  className={`action-button ${action.variant || 'primary'}`}
                  onClick={action.onClick}
                  title={action.label}
                  disabled={action.disabled}
                >
                  {action.icon && <span className="icon">{action.icon}</span>}
                  {action.label && <span>{action.label}</span>}
                </button>
              ))
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="main-content-scroll-area">
        <div className="content-container">

          {/* Primary Child Content */}
          <div className="details-primary-content">
            {children}
          </div>

          {/* Extensible Resources Section (AI/Custom Resources) */}
          {resourcesSection && (
            <div className="details-section resources-section">
              {resourcesSection}
            </div>
          )}

          {/* Related Data Sections */}
          <div className="details-section related-data-section">
            {showRelatedDocuments && (
              <RelatedDocumentsSection
                contentBlocks={contentBlocks}
                tripId={tripId}
                onUnlink={onUnlink}
              />
            )}

            {showRelatedEntities && (
              <RelatedEntitiesSection
                contentBlocks={contentBlocks}
                tripId={tripId}
                onUnlink={onUnlink}
              />
            )}
          </div>

          {/* Notes Section */}
          {onSaveNotes && (
            <div className="details-section notes-section">
              <Notes
                content={notes || ''}
                onSave={onSaveNotes}
                lastEditedTime={lastEditedTime}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

StandardDetailsLayout.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onBack: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    variant: PropTypes.string,
    disabled: PropTypes.bool
  })),
  headerActions: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node,
  resourcesSection: PropTypes.node,
  notes: PropTypes.string,
  onSaveNotes: PropTypes.func,
  lastEditedTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  contentBlocks: PropTypes.array,
  tripId: PropTypes.string,
  onUnlink: PropTypes.func,
  showRelatedDocuments: PropTypes.bool,
  showRelatedEntities: PropTypes.bool,
  className: PropTypes.string
};
